/**
 * The driver's route sheet, option 1 of the print canvas ("Timeline", Abner's
 * pick, Sep 25): the Delivery page on paper, one sheet per delivery day. It
 * prints from the Delivery page (Print route) and as the Orders packet's last
 * section.
 *
 *   Delivery route                                                Page 32 of 34
 *   Delivery · Jeff's Over the Top Catering · Deliveries Fri, Sep 25 · Printed …
 *   ──────────────────────────────────────────────────────────────────────────
 *     Leave     (⚑)  Jeff's Over the Top Catering                        [QR]
 *    by 8:30 AM  │   25890 Middlebelt Rd., Farmington Hills, MI 48336
 *                │   To reach Heartfelt Impressions before its window closes at 9:30 AM.
 *                │   5 stops · 15 min at each stop · In delivery-window order
 *    7:00 AM    (1)  OLHSA Pontiac Main Building
 *   to 9:00 AM   │   Oakland Livingston Human Service Agency
 *                │   Address  196 Cesar E Chavez, Pontiac, MI 48342
 *                │   Window   7:00 to 9:00 AM
 *                │   Contact  Terry Nolan · (248) 555-0147
 *                │   1  Verify that all special diet meals …
 *                │   WHAT TO DELIVER
 *                │       Package            Content
 *                │   Hot · 2
 *                │   ☐ 2 Bulk Trays         335x Corn, Seasoned                Lunch
 *
 * The same words as the screen: the time column (`leaveTimeColumn`,
 * `stopTimeColumn`), the leave node's reason and settings line, each stop's
 * Address / Window / Contact and its instructions read line by line
 * (`instructionItems`), and "What to deliver" as Abner's target print has it
 * (137:1699): milk first, on its own with "Provide supply for X days", then
 * the carriers, each row [check] / packages / content ("2 Bulk Trays | 335x
 * Corn, Seasoned"), the meal on the right (`routeDeliverGroups`). The screen's
 * Open Maps is a QR code of the same link. The header rows repeat on every
 * printed page (`<thead>`) and "Page N of M" sits in the margin, like every
 * chef packet (Hunter, Sep 18: sheets fall out of the stack). "Received" is
 * left off: the sheet is printed before the drive.
 */

import { cn } from '@ordo/ui/cn'
import { Flag, FlaskConical } from 'lucide-react'
import type { ReactNode } from 'react'

import type {
  DeliveryRouteDay,
  DeliveryRouteDriveStatus,
  DeliveryRouteResponse,
  DeliveryRouteStop,
} from '@/api/delivery-route'
import { PRINT_SECONDARY, PrintSheetHeading } from '@/components/chefs/print/print-sheet-heading'
import { usePrintedAt } from '@/hooks/chefs/usePrintedAt'
import {
  instructionItems,
  leaveTimeColumn,
  mealStartsLine,
  orderLine,
  phoneLink,
  routeSummaryCopy,
  sentenceStart,
  stopTimeColumn,
  stopTimeCopy,
  type TimeColumn,
  windowLabel,
} from '@/lib/chefs/delivery-route'
import {
  deliveriesPrintSubheading,
  deliveryPrintHeading,
  ORDO_PACKET_PAGE_CLASS,
  sheetPrintSubheading,
} from '@/lib/chefs/print-identity'
import { routeDeliverGroups } from '@/lib/chefs/print-packages'
import { qrPath } from '@/lib/chefs/route-qr'
import type { Campus } from '@/lib/chefs/types'

const COLUMNS = 3

export interface PrintRouteDay {
  day: DeliveryRouteDay
  /** Local midnight of the delivery day, for the printed labels. */
  date: Date
  campusesById: ReadonlyMap<string, Campus>
}

/** Where the rail runs in a row's marker cell: the whole row, below the marker, down to it, or not at all. */
export type RailSpan = 'full' | 'below' | 'above' | 'none'

/**
 * The rail between the markers, as on the screen's timeline: from the first
 * node's marker to the last one's. A node's first row holds its marker; the
 * rows under it (a stop's load) carry the rail on to the next node.
 */
export function railSpan(nodeIndex: number, nodeCount: number, markerRow: boolean): RailSpan {
  const last = nodeIndex === nodeCount - 1
  if (nodeCount <= 1) {
    return 'none'
  }
  if (!markerRow) {
    return last ? 'none' : 'full'
  }
  return nodeIndex === 0 ? 'below' : last ? 'above' : 'full'
}

const RAIL_CLASS: Record<Exclude<RailSpan, 'none'>, string> = {
  full: 'inset-y-0',
  below: 'top-[14px] bottom-0',
  above: 'top-0 h-[14px]',
}

/** The marker column: the rail, and the node's marker over it. */
function RailCell({ span, marker }: { span: RailSpan; marker?: ReactNode }) {
  return (
    <td className="relative align-top">
      {span !== 'none' ? (
        <span aria-hidden className={cn('absolute left-1/2 w-px -translate-x-1/2 bg-[#888]', RAIL_CLASS[span])} />
      ) : null}
      {marker ? (
        <span className="relative mx-auto flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-black bg-white text-[9pt] font-semibold tabular-nums">
          {marker}
        </span>
      ) : null}
    </td>
  )
}

/** The time column: the big word, and what qualifies it under it. */
function TimeCell({ column }: { column?: TimeColumn }) {
  return (
    <td className="pr-2 pt-0.5 align-top">
      {column?.time ? <div className="font-semibold tabular-nums">{column.time}</div> : null}
      {column?.detail ? <div className={PRINT_SECONDARY}>{column.detail}</div> : null}
    </td>
  )
}

/** The screen's Open Maps as a QR code: the same Google Maps link. */
export function RouteQr({ url, label }: { url: string; label: string }) {
  const qr = qrPath(url)
  if (!qr) {
    return null
  }
  const box = qr.size + 4
  return (
    <div data-testid="route-qr" className="flex w-[112px] flex-col items-center gap-1">
      <svg
        role="img"
        aria-label={label}
        viewBox={`-2 -2 ${box} ${box}`}
        width={104}
        height={104}
        shapeRendering="crispEdges">
        <rect x={-2} y={-2} width={box} height={box} fill="#ffffff" />
        <path d={qr.d} fill="#000000" />
      </svg>
      <span className={cn('text-center leading-tight', PRINT_SECONDARY)}>{label}</span>
    </div>
  )
}

/** The QR codes' captions: one link, or a long day split into parts (Google's waypoint limit), as the screen's Open Maps buttons. */
export function routeQrLabels(count: number): string[] {
  return Array.from({ length: count }, (_, i) =>
    count === 1 ? 'Scan to open the route in Google Maps' : `Part ${i + 1} of ${count}: scan to open in Google Maps`,
  )
}

/** A stop's where, when and who: the panel's labelled column. */
function StopFields({ stop }: { stop: DeliveryRouteStop }) {
  const window = windowLabel(stop.window)
  const meals = mealStartsLine(stop)
  const phone = phoneLink(stop.contactPhone)
  const rows: Array<{ label: string; value: ReactNode }> = [
    {
      label: 'Address',
      value: stop.address ? stop.address.text : 'No address on file. Ordo support can add it.',
    },
  ]
  if (window) {
    rows.push({
      label: 'Window',
      value: (
        <>
          <span className="font-semibold">{window}</span>
          {meals ? <span className="text-[#222]"> · {meals}</span> : null}
        </>
      ),
    })
  } else if (meals) {
    rows.push({ label: 'Meals', value: meals })
  }
  if (stop.contactName || phone) {
    rows.push({ label: 'Contact', value: [stop.contactName, phone?.label].filter(Boolean).join(' · ') })
  }
  return (
    <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-y-px pt-1.5 text-[9.5pt]">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-[#444]">{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** The school's instructions, read line by line: a step hangs from its number, a field shows its label, a note stands alone. */
function StopInstructionLines({ stop }: { stop: DeliveryRouteStop }) {
  const items = instructionItems(stop.instructions)
  if (items.length === 0) {
    return <p className={cn('pt-1.5', PRINT_SECONDARY)}>No delivery instructions on file for this school.</p>
  }
  return (
    <ul className="flex flex-col gap-px pt-1.5 text-[9.5pt] text-[#222]">
      {items.map((item, i) => (
        <li key={i} data-kind={item.kind} className="grid grid-cols-[16px_minmax(0,1fr)]">
          <span className="font-semibold tabular-nums text-[#666]">{item.kind === 'step' ? item.number : '•'}</span>
          {item.kind === 'field' ? (
            <span>
              <span className="text-[#444]">{item.label} </span>
              <span className="font-medium text-black">{item.value}</span>
            </span>
          ) : (
            <span>{item.text}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/** One stop's rows: its marker row with where / when / who / how, then what to unload, carrier by carrier. */
function StopRows({
  stop,
  n,
  campus,
  deliveryDate,
  nodeIndex,
  nodeCount,
}: {
  stop: DeliveryRouteStop
  n: number
  campus: Campus | undefined
  deliveryDate: string
  nodeIndex: number
  nodeCount: number
}) {
  const note = stopTimeCopy(stop).note
  const sections = routeDeliverGroups(campus, deliveryDate)
  const body = railSpan(nodeIndex, nodeCount, false)
  return (
    <tbody data-testid="route-stop">
      <tr className="break-inside-avoid">
        <TimeCell column={stopTimeColumn(stop)} />
        <RailCell span={railSpan(nodeIndex, nodeCount, true)} marker={n} />
        <td className="pb-1 pl-2.5 align-top">
          <div className="text-[12pt] font-semibold leading-tight">{stop.name}</div>
          {stop.organizationName && stop.organizationName !== stop.name ? (
            <div className={PRINT_SECONDARY}>{stop.organizationName}</div>
          ) : null}
          <StopFields stop={stop} />
          {note ? <p className="pt-1 text-[9.5pt] font-medium">{note}</p> : null}
          {stop.dryRun === 'TASTING' ? (
            <p className="pt-1 text-[9.5pt] font-medium">Tasting: deliver as live service.</p>
          ) : null}
          {stop.windowNote ? (
            <p className="pt-1 text-[9.5pt]">
              <span className="text-[#444]">Delivery window, as the school wrote it: </span>
              {stop.windowNote}
            </p>
          ) : null}
          <StopInstructionLines stop={stop} />
        </td>
      </tr>
      <tr className="break-after-avoid">
        <td />
        <RailCell span={body} />
        <td className="pl-2.5 pt-2 text-[9pt] font-semibold uppercase tracking-wide">What to deliver</td>
      </tr>
      {sections.length === 0 ? (
        <tr>
          <td />
          <RailCell span={body} />
          <td className={cn('pl-2.5', PRINT_SECONDARY)}>Nothing to unload here.</td>
        </tr>
      ) : null}
      {sections.length > 0 ? (
        <tr className="break-after-avoid">
          <td />
          <RailCell span={body} />
          <td className="pl-2.5">
            <div className="grid grid-cols-[14px_minmax(0,38%)_minmax(0,1fr)_auto] gap-x-2 border-b border-black pb-0.5 pt-1 text-[9pt] font-medium text-[#444]">
              <span />
              <span>Package</span>
              <span>Content</span>
              <span />
            </div>
          </td>
        </tr>
      ) : null}
      {sections.map((section) => [
        <tr key={`${section.key}-heading`} className="break-after-avoid">
          <td />
          <RailCell span={body} />
          <td className="pl-2.5">
            <div className="border-b border-black pb-0.5 pt-1.5 text-[9pt] font-semibold">
              {section.label} · {section.rows.length}
              {section.note ? <span className="font-normal text-[#444]"> · {section.note}</span> : null}
            </div>
          </td>
        </tr>,
        ...section.rows.map((row) => (
          <tr key={row.key} data-testid="route-load-row" className="break-inside-avoid">
            <td />
            <RailCell span={body} />
            <td className="pl-2.5">
              <div className="grid grid-cols-[14px_minmax(0,38%)_minmax(0,1fr)_auto] items-start gap-x-2 border-b border-[#ddd] py-0.5">
                <span aria-hidden className="mt-[3px] block h-[11px] w-[11px] border border-black" />
                <span data-testid="route-load-package" className="font-semibold">
                  {row.packageLabel}
                </span>
                <span data-testid="route-load-content">{row.content}</span>
                {row.tag ? <span className={cn('whitespace-nowrap', PRINT_SECONDARY)}>{row.tag}</span> : <span />}
              </div>
            </td>
          </tr>
        )),
      ])}
      <tr aria-hidden>
        <td />
        <RailCell span={body} />
        <td className="h-4" />
      </tr>
    </tbody>
  )
}

export function PrintRouteSheet({
  routeDay,
  kitchen,
  stopMinutes,
  driveStatus,
}: {
  routeDay: PrintRouteDay
  kitchen: DeliveryRouteResponse['kitchen']
  stopMinutes: number
  driveStatus: DeliveryRouteDriveStatus
}) {
  const printedAt = usePrintedAt()
  const { day, date, campusesById } = routeDay
  const copy = routeSummaryCopy(day, driveStatus)
  const hasLeave = day.stops.length > 0
  const hasNotDriven = day.notDriven.length > 0
  const nodeCount = (hasLeave ? 1 : 0) + day.stops.length + (hasNotDriven ? 1 : 0)
  const firstStop = hasLeave ? 1 : 0
  const labels = routeQrLabels(day.googleMapsUrls.length)
  const stopCount = day.stops.length
  return (
    <div className="font-sans text-[10.5pt] leading-snug text-black">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[84px]" />
          <col className="w-[28px]" />
          <col />
        </colgroup>
        <thead>
          <PrintSheetHeading
            title="Delivery route"
            subheading={sheetPrintSubheading(deliveryPrintHeading(kitchen.name), date, printedAt)}
            warning={copy.warning}
            colSpan={COLUMNS}
          />
          <tr aria-hidden>
            <th colSpan={COLUMNS} className="border-t border-black pb-3" />
          </tr>
        </thead>
        {hasLeave ? (
          <tbody data-testid="route-leave">
            <tr className="break-inside-avoid">
              <TimeCell column={leaveTimeColumn(copy)} />
              <RailCell span={railSpan(0, nodeCount, true)} marker={<Flag className="h-3 w-3" strokeWidth={2} />} />
              <td className="pb-4 pl-2.5 align-top">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <div className="font-semibold">{kitchen.name}</div>
                    {kitchen.address ? <div className={PRINT_SECONDARY}>{kitchen.address.text}</div> : null}
                    <p className="pt-0.5 text-[9.5pt] text-[#222]">{sentenceStart(copy.reason)}</p>
                    <p className={PRINT_SECONDARY}>
                      {[
                        stopCount === 1 ? '1 stop' : `${stopCount} stops`,
                        `${stopMinutes} min at each stop`,
                        orderLine(day),
                      ].join(' · ')}
                    </p>
                    {driveStatus === 'OK' && day.plan.status !== 'NEEDS_DRIVE_TIMES' ? (
                      <p className={PRINT_SECONDARY}>Drive times from Google Maps, without traffic.</p>
                    ) : null}
                  </div>
                  {day.googleMapsUrls.length > 0 ? (
                    <div className="flex shrink-0 gap-2">
                      {day.googleMapsUrls.map((url, i) => (
                        <RouteQr key={url} url={url} label={labels[i]!} />
                      ))}
                    </div>
                  ) : null}
                </div>
              </td>
            </tr>
          </tbody>
        ) : null}
        {day.stops.map((stop, i) => (
          <StopRows
            key={stop.schoolLocationId}
            stop={stop}
            n={i + 1}
            campus={campusesById.get(stop.schoolLocationId)}
            deliveryDate={day.date}
            nodeIndex={firstStop + i}
            nodeCount={nodeCount}
          />
        ))}
        {hasNotDriven ? (
          <tbody data-testid="route-not-driven">
            <tr className="break-inside-avoid">
              <td />
              <RailCell
                span={railSpan(nodeCount - 1, nodeCount, true)}
                marker={<FlaskConical className="h-3 w-3" strokeWidth={2} />}
              />
              <td className="pl-2.5 align-top">
                <div className="pt-0.5 font-semibold">Not on the route</div>
                {day.notDriven.map((stop) => (
                  <p key={stop.schoolLocationId} className="pt-0.5 text-[9.5pt]">
                    <span className="font-semibold">{stop.name}</span>: dry run. Cook and stage it at the kitchen, do
                    not deliver.
                  </p>
                ))}
              </td>
            </tr>
          </tbody>
        ) : null}
      </table>
    </div>
  )
}

/** The Orders packet's Delivery route section when the route could not load: one band, on its own page. */
export function PrintRouteUnavailable({
  kitchenName,
  firstDay,
  lastDay,
}: {
  kitchenName: string
  firstDay: Date
  lastDay: Date
}) {
  const printedAt = usePrintedAt()
  return (
    <div className="font-sans text-[10.5pt] leading-snug text-black">
      <table className="w-full border-collapse">
        <thead>
          <PrintSheetHeading
            title="Delivery route"
            subheading={`${deliveryPrintHeading(kitchenName)} · ${deliveriesPrintSubheading(firstDay, lastDay, printedAt)}`}
            colSpan={1}
          />
        </thead>
      </table>
      <p className="mt-2 rounded border border-black p-4 text-[11pt]">
        The delivery route could not load. Turn off Delivery route to print without it.
      </p>
    </div>
  )
}

/** Every day's sheet, one after another, on the chef packet's named page: the Delivery page's own print. */
export function PrintRouteSheets({
  days,
  kitchen,
  stopMinutes,
  driveStatus,
}: {
  days: readonly PrintRouteDay[]
  kitchen: DeliveryRouteResponse['kitchen']
  stopMinutes: number
  driveStatus: DeliveryRouteDriveStatus
}) {
  return (
    <div className={ORDO_PACKET_PAGE_CLASS}>
      {days.map((routeDay, i) => (
        <div key={routeDay.day.date} className={i < days.length - 1 ? 'print-page-break' : undefined}>
          <PrintRouteSheet routeDay={routeDay} kitchen={kitchen} stopMinutes={stopMinutes} driveStatus={driveStatus} />
        </div>
      ))}
    </div>
  )
}
